import React from 'react';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-900/30 to-gray-900 py-24 text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-blue-400 mb-8">
            Turing Academy
          </h2>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-100 mb-6">
            Prepare Your Child for the AI-Driven Future
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Give your child the competitive edge with hands-on AI development and research skills
          </p>
          <a href="https://docs.google.com/forms/d/e/1FAIpQLSfcJaU4SsulXYXfbe0Oj2b3XVpDFBpbVOtEJBarjS4g-S5k2g/viewform" 
             className="inline-block bg-blue-700 hover:bg-blue-800 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg">
            Schedule Call
          </a>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-100 mb-12">
            Why Early AI Education Matters
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-700 rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 text-center hover:bg-gray-600 hover:-translate-y-1">
              <h3 className="text-2xl font-bold text-blue-400 mb-4">Early Exposure Creates Lifelong Advantages</h3>
              <p className="text-gray-300">
                Research shows that students who learn coding and AI concepts at a young age are more likely to excel in problem-solving and critical thinking—skills essential for future success.
              </p>
            </div>
            <div className="bg-gray-700 rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 text-center hover:bg-gray-600 hover:-translate-y-1">
              <h3 className="text-2xl font-bold text-blue-400 mb-4">Increasing Integration</h3>
              <p className="text-gray-300">
                <b style={{ color: 'powderblue' }}>Universities and employers are prioritizing candidates with AI skills.</b> A LinkedIn report identified AI as one of the top 10 most in-demand skills globally. Over 60% of global organizations are actively adopting AI technologies.
              </p>
            </div>
            <div className="bg-gray-700 rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 text-center hover:bg-gray-600 hover:-translate-y-1">
              <h3 className="text-2xl font-bold text-blue-400 mb-4">Future Opportunities</h3>
              <p className="text-gray-300">
                Learning AI gives students a head start in building portfolios that stand out. Over 30% of STEM internships now require AI knowledge, and this number is growing rapidly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-100 mb-12 hover:text-blue-400 transition-colors duration-300">
            The Growing Impact of AI
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { number: "37.3%", text: "Annual growth rate in AI industry (2023-2030)" },
              { number: "$100K+", text: "Starting salary for entry-level AI roles" },
              { number: "97M", text: "New AI jobs emerging by 2025" },
            ].map((stat, index) => (
              <div key={index} className="bg-gray-800 rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-all duration-300 hover:bg-gray-700 hover:-translate-y-1">
                <h3 className="text-4xl font-bold text-blue-400 mb-4 hover:text-blue-300 transition-colors duration-300">{stat.number}</h3>
                <p className="text-gray-300">{stat.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-100 mb-12">
            How We Prepare Your Child for Success
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                title: "Practical AI Development", 
                description: "Learn to build real AI tools using cutting-edge libraries",
                image: "/aitools.webp"
              },
              { 
                title: "Portfolio Projects", 
                description: "Create impressive AI projects that stand out on college applications",
                image: "/bttr.png"
              },
              { 
                title: "Research Experience", 
                description: "Gain hands-on experience with AI research and experimentation",
                image: "/gauthamresearch.jpg",
                imagePosition: "object-top"
              },
            ].map((feature, index) => (
              <div key={index} className="bg-gray-700 rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-all duration-300 hover:bg-gray-600 hover:-translate-y-1">
                {feature.image && (
                  <img 
                    src={feature.image} 
                    alt={feature.title}
                    className={`w-full h-56 object-cover rounded-lg mb-6 ${feature.imagePosition || 'object-center'}`}
                  />
                )}
                <h3 className="text-2xl font-bold text-gray-100 mb-4">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Why Choose Us Subsection */}
          <div className="bg-gray-700/50 rounded-2xl p-8 mt-12">
            <h3 className="text-3xl font-bold text-center text-blue-400 mb-8">
              Why Choose Our Program?
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Expert Instructors",
                  description: "Learn from industry professionals and AI researchers with years of real-world experience and research that has garnered news coverage"
                },
                {
                  title: "Small Class Sizes",
                  description: "Maximum 4:1 student-to-teacher ratio ensures personalized attention and optimal learning"
                },
                {
                  title: "Project-Based Learning",
                  description: "Students work on real-world AI projects, not just theoretical concepts"
                },
                {
                  title: "Industry Partnerships",
                  description: "Access to cutting-edge tools and technologies through our dealings with leading AI companies"
                },
                {
                  title: "Proven Curriculum",
                  description: "Structured learning path developed in consultation with educational experts and AI professionals"
                },
                {
                  title: "Ongoing Support",
                  description: "Regular progress updates, mentorship sessions, and post-program guidance for landing internships"
                }
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-4 p-4 bg-gray-700 rounded-lg">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-900/50 flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-blue-400 mb-2">{item.title}</h4>
                    <p className="text-gray-300">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Affiliations Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-100 mb-12">
            Affiliations
          </h2>
          <p className="text-xl text-gray-300 text-center mb-12">
            Institutions associated with some of our instructors' research efforts
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-700 rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-all duration-300 hover:bg-gray-600 hover:-translate-y-1">
              <img src="/harvard.png" alt="Harvard" className="w-full h-40 object-contain rounded-lg mb-6" />
            </div>
            <div className="bg-gray-700 rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-all duration-300 hover:bg-gray-600 hover:-translate-y-1">
              <img src="/umd.png" alt="UMD" className="w-full h-40 object-contain rounded-lg mb-6" />
            </div>
            <div className="bg-gray-700 rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-all duration-300 hover:bg-gray-600 hover:-translate-y-1">
              <img src="/gmu.jpg" alt="GMU" className="w-full h-40 object-contain rounded-lg mb-6" />
            </div>
          </div>
        </div>
      </section>

      {/* Investment Section */}
      <section className="bg-blue-900 py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Invest in Your Child's Future Success
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-200">
            Join our program and give your child the skills they need to thrive in an AI-driven world
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-100 mb-12">
            Simple, Transparent Pricing
          </h2>
          <div className="max-w-2xl mx-auto">
            <div className="bg-gray-700 rounded-xl shadow-lg p-12 text-center hover:shadow-xl transition-all duration-300 hover:bg-gray-600 hover:-translate-y-1">
              <div className="text-5xl font-bold text-gray-100 mb-4">$30<span className="text-2xl text-gray-400">/hour</span></div>
              <ul className="text-gray-300 space-y-4 mb-8 text-lg">
                <li>Personalized 1-on-1 Instruction</li>
                <li>Flexible Scheduling</li>
                <li>Project-Based Learning</li>
                <li>Access to AI Tools & Resources</li>
              </ul>
              <a href="https://docs.google.com/forms/d/e/1FAIpQLSfcJaU4SsulXYXfbe0Oj2b3XVpDFBpbVOtEJBarjS4g-S5k2g/viewform" 
                 className="inline-block bg-blue-700 hover:bg-blue-800 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg">
                Schedule Call
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bg-blue-900 py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

        </div>
      </section>

    </div>
  );
};

export default LandingPage;